import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  HiCheckCircle,
  HiOutlineArrowRight,
  HiOutlineBriefcase,
  HiOutlineSparkles,
  HiOutlineUserGroup,
} from "react-icons/hi";
import "../styles/variables.css";
import "../styles/MultiMatters.css";
import { useMatter } from "../context/MatterContext";
import { useModules } from "../context/ModulesContext";
import NoOpenMatterPage from "./NoOpenMatterPage";
import { shouldShowProjectRFlowForMatter } from "../modules/moduleUtils";

const SUPPORTED_CASE_TYPES = new Set([
  "bhp-individuals",
  "bhp-indigenous",
  "mariana 2026",
  "project unicorn",
  "project unicorn ii",
]);

const ENGLISH_CLAIM_CASE_TYPES = new Set([
  "bhp-individuals",
  "bhp-indigenous",
  "mariana 2026",
]);

const NETHERLANDS_CLAIM_CASE_TYPES = new Set([
  "project unicorn",
  "project unicorn ii",
]);

const isSupportedMatter = (matter) => {
  const caseType = String(matter?.caseTypeName ?? "").trim().toLowerCase();
  return SUPPORTED_CASE_TYPES.has(caseType);
};

const hasPendingTermination = (party) => {
  const compensation = party?.compensation;
  if (!compensation) return false;
  const schemes = [compensation.pid, compensation.ff, compensation.novel];
  return schemes.some((scheme) => {
    if (!scheme) return false;
    return scheme.agreementDate || scheme.paymentDate || scheme.value != null;
  });
};

const resolveEntryPath = (party, modules, matterId) => {
  if (hasPendingTermination(party)) {
    return "/dashboard";
  }
  return shouldShowProjectRFlowForMatter(modules, matterId)
    ? "/important"
    : "/dashboard";
};

const getPartyId = (party) => {
  return party?.id || party?.partyId || party?.Id || "";
};

const getMatterReference = (matter) => {
  return String(
    matter?.name || matter?.matterNumber || matter?.matterId || matter?.id || "",
  ).trim();
};

const getClientFacingCaseTypeLabel = (caseTypeName) => {
  const normalizedCaseType = String(caseTypeName || "").trim().toLowerCase();

  if (ENGLISH_CLAIM_CASE_TYPES.has(normalizedCaseType)) {
    return "Caso Inglês";
  }

  if (NETHERLANDS_CLAIM_CASE_TYPES.has(normalizedCaseType)) {
    return "Caso Holandês";
  }

  return String(caseTypeName || "").trim();
};

const getStepCopy = ({
  hasSelectedParty,
  activePartyName,
  totalPartyCount,
}) => {
  if (!hasSelectedParty) {
    return {
      stepLabel: "Selecionar parte",
      progressValue: totalPartyCount > 1 ? 50 : 100,
      title: "Escolha a parte que deseja acessar",
      subtitle:
        "Selecione o perfil correto para continuarmos com os casos disponiveis.",
      badge: "Selecao segura",
      icon: <HiOutlineUserGroup />,
    };
  }

  return {
    stepLabel: "Selecionar caso",
    progressValue: 100,
    title: "Escolha o caso que deseja acessar",
    subtitle: activePartyName
      ? `Parte selecionada: ${activePartyName}`
      : "Selecione o caso que deseja abrir no portal.",
    badge: "Pronto para entrar",
    icon: <HiOutlineBriefcase />,
  };
};

const MultiMatters = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    parties,
    party,
    matter,
    selectedPartyId,
    selectedMatterId,
    selectedPartyScope,
    selectParty,
    selectMatter,
    bootstrap,
    bootstrapping,
    bootstrapState,
  } = useMatter();
  const { modules, modulesInitialized } = useModules();
  const [initializing, setInitializing] = useState(true);
  const shouldAutoEnterPortal = location.pathname === "/";

  useEffect(() => {
    let cancelled = false;

    const ensureBootstrapped = async () => {
      const result = await bootstrap();
      if (cancelled) return;
      if (!result?.ok) {
        if (result?.reason === "no_open_matter") {
          setInitializing(false);
          return;
        }
        navigate("/signin", { replace: true });
        return;
      }
      setInitializing(false);
    };

    if (bootstrapState === "ready" || bootstrapState === "error") {
      setInitializing(false);
      return () => {
        cancelled = true;
      };
    }

    if (!bootstrapping && bootstrapState !== "no_open_matter") {
      ensureBootstrapped();
    }

    return () => {
      cancelled = true;
    };
  }, [bootstrapping, bootstrapState, bootstrap, navigate]);

  useEffect(() => {
    if (bootstrapState === "no_open_matter") {
      setInitializing(false);
    }
  }, [bootstrapState]);

  const decoratedPartyScopes = useMemo(
    () =>
      (Array.isArray(parties) ? parties : []).map((scope) => ({
        ...scope,
        supportedMatters: (Array.isArray(scope?.matters) ? scope.matters : []).filter(
          isSupportedMatter,
        ),
      })),
    [parties],
  );

  const activePartyScope = useMemo(() => {
    if (!selectedPartyId) return null;
    return (
      decoratedPartyScopes.find((scope) => getPartyId(scope?.party) === selectedPartyId) ||
      null
    );
  }, [decoratedPartyScopes, selectedPartyId]);

  const supportedMatters = useMemo(
    () => activePartyScope?.supportedMatters || [],
    [activePartyScope],
  );
  const stepCopy = useMemo(
    () =>
      getStepCopy({
        hasSelectedParty: Boolean(selectedPartyId),
        activePartyName: activePartyScope?.party?.name || "",
        totalPartyCount: decoratedPartyScopes.length,
      }),
    [selectedPartyId, activePartyScope, decoratedPartyScopes.length],
  );

  const navigateToEntryPath = useCallback(
    (currentParty, selectedMatterValue) => {
      navigate(resolveEntryPath(currentParty, modules, selectedMatterValue), {
        replace: true,
      });
    },
    [modules, navigate],
  );

  useEffect(() => {
    if (!shouldAutoEnterPortal || initializing || bootstrapping || !modulesInitialized) {
      return;
    }
    if (!selectedPartyScope || !matter) return;
    navigateToEntryPath(selectedPartyScope.party, matter?.id || "");
  }, [
    shouldAutoEnterPortal,
    initializing,
    bootstrapping,
    modulesInitialized,
    selectedPartyScope,
    matter,
    navigateToEntryPath,
  ]);

  useEffect(() => {
    if (initializing || bootstrapping || !activePartyScope) return;

    if (supportedMatters.length !== 1) {
      return;
    }

    const onlyMatterId = supportedMatters[0]?.id || "";
    if (!onlyMatterId) return;

    if (selectedMatterId !== onlyMatterId) {
      selectMatter(onlyMatterId);
    }
  }, [
    initializing,
    bootstrapping,
    activePartyScope,
    supportedMatters,
    selectedMatterId,
    selectMatter,
  ]);

  const handleSelectParty = (partyId) => {
    if (!partyId) return;
    selectParty(partyId);
  };

  const handleSelectMatter = (matterId) => {
    if (!matterId || !selectedPartyScope) return;
    selectMatter(matterId);
    navigateToEntryPath(party || selectedPartyScope.party, matterId);
  };

  if (initializing || bootstrapping || !modulesInitialized) {
    return (
      <div className="multimatters-page multimatters-page--loading">
        <div className="multimatters-container">
          <div className="multimatters-loading">
            <div className="multimatters-spinner" aria-label="Carregando" />
            <p className="multimatters-loading-text">Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  if (bootstrapState === "no_open_matter") {
    return <NoOpenMatterPage />;
  }

  if (decoratedPartyScopes.length === 0) {
    return (
      <div className="multimatters-page">
        <div className="multimatters-container multimatters-container--panel">
          <div className="multimatters-topbar">
            <div className="multimatters-stepBadge">Acesso ao portal</div>
          </div>
          <h1 className="multimatters-heading">Nenhum acesso encontrado</h1>
          <p className="multimatters-text">
            Nao encontramos partes disponiveis para a sua conta.
          </p>
        </div>
      </div>
    );
  }

  if (!selectedPartyId) {
    return (
      <div className="multimatters-page">
        <div className="multimatters-container multimatters-container--panel">
          <div className="multimatters-topbar">
            <div className="multimatters-stepBadge">{stepCopy.stepLabel}</div>
            <div className="multimatters-stepPill">
              <HiOutlineSparkles />
              <span>{stepCopy.badge}</span>
            </div>
          </div>

          <div className="multimatters-progress" aria-hidden="true">
            <div
              className="multimatters-progressFill"
              style={{ width: `${stepCopy.progressValue}%` }}
            />
          </div>

          <div className="multimatters-hero">
            <div className="multimatters-heroIcon">{stepCopy.icon}</div>
            <div className="multimatters-heroText">
              <h1 className="multimatters-heading">{stepCopy.title}</h1>
              <p className="multimatters-text">{stepCopy.subtitle}</p>
            </div>
          </div>

          <div className="multimatters-cards">
            {decoratedPartyScopes.map((scope, index) => {
              const partyId = getPartyId(scope?.party);
              const matterReferences = scope.supportedMatters
                .map(getMatterReference)
                .filter(Boolean);
              return (
                <button
                  key={partyId}
                  type="button"
                  className="multimatters-card multimatters-card--party"
                  onClick={() => handleSelectParty(partyId)}
                  style={{ animationDelay: `${index * 70}ms` }}
                >
                  <div className="multimatters-cardGlow" aria-hidden="true" />
                  <div className="multimatters-cardAccent" aria-hidden="true" />
                  <div className="multimatters-card-text">
                    <div className="multimatters-card-kicker">Perfil disponivel</div>
                    <div className="multimatters-card-brand">
                      {scope?.party?.name || "Parte sem nome"}
                    </div>
                    {matterReferences.length ? (
                      <div className="multimatters-matterReferences">
                        <span className="multimatters-matterReferences-label">
                          Processos vinculados
                        </span>
                        <div
                          className={`multimatters-matterReferences-list ${
                            matterReferences.length > 5
                              ? "multimatters-matterReferences-list--scrollable"
                              : ""
                          }`}
                          aria-label={`Processos vinculados: ${matterReferences.join(", ")}`}
                        >
                          {matterReferences.map((reference) => (
                            <span
                              key={reference}
                              className="multimatters-matterReference"
                            >
                              {reference}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="multimatters-card-right">
                    <div className="multimatters-cardArrow">
                      <HiOutlineArrowRight />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (!activePartyScope) {
    return (
      <div className="multimatters-page">
        <div className="multimatters-container multimatters-container--panel">
          <h1 className="multimatters-heading">Selecao invalida</h1>
          <p className="multimatters-text">
            A parte selecionada nao esta mais disponivel.
          </p>
          <button
            type="button"
            className="multimatters-secondary"
            onClick={() => selectParty(null)}
          >
            Escolher outra parte
          </button>
        </div>
      </div>
    );
  }

  if (supportedMatters.length === 0) {
    return (
      <div className="multimatters-page">
        <div className="multimatters-container multimatters-container--panel">
          <div className="multimatters-topbar">
            <div className="multimatters-stepBadge">{stepCopy.stepLabel}</div>
          </div>
          <h1 className="multimatters-heading">Nenhum caso disponivel</h1>
          <p className="multimatters-text">
            Nao encontramos casos elegiveis para esta parte.
          </p>
          {decoratedPartyScopes.length > 1 ? (
            <button
              type="button"
              className="multimatters-secondary"
              onClick={() => selectParty(null)}
            >
              Escolher outra parte
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="multimatters-page">
      <div className="multimatters-container multimatters-container--panel">
        <div className="multimatters-topbar">
          <div className="multimatters-stepBadge">{stepCopy.stepLabel}</div>
          <div className="multimatters-stepPill">
            <HiCheckCircle />
            <span>{stepCopy.badge}</span>
          </div>
        </div>

        <div className="multimatters-progress" aria-hidden="true">
          <div
            className="multimatters-progressFill"
            style={{ width: `${stepCopy.progressValue}%` }}
          />
        </div>

        <div className="multimatters-hero">
          <div className="multimatters-heroIcon">{stepCopy.icon}</div>
          <div className="multimatters-heroText">
            <h1 className="multimatters-heading">{stepCopy.title}</h1>
            <p className="multimatters-text">{stepCopy.subtitle}</p>
          </div>
        </div>

        {decoratedPartyScopes.length > 1 ? (
          <button
            type="button"
            className="multimatters-secondary"
            onClick={() => selectParty(null)}
          >
            Trocar parte
          </button>
        ) : null}

        <div className="multimatters-cards">
          {supportedMatters.map((currentMatter, index) => (
            <button
              key={currentMatter.id}
              type="button"
              className={`multimatters-card multimatters-card--matter ${
                selectedMatterId === currentMatter.id ? "selected" : ""
              }`}
              onClick={() => handleSelectMatter(currentMatter.id)}
              style={{ animationDelay: `${index * 70}ms` }}
            >
              <div className="multimatters-cardGlow" aria-hidden="true" />
              <div className="multimatters-cardAccent" aria-hidden="true" />
              <div className="multimatters-card-text">
                <div className="multimatters-card-kicker">Caso disponivel</div>
                <div className="multimatters-card-brand">{currentMatter.name}</div>
                <div className="multimatters-card-title">
                  {getClientFacingCaseTypeLabel(currentMatter.caseTypeName)}
                </div>
              </div>
              <div className="multimatters-card-right">
                <div className="multimatters-selector">
                  <div className="multimatters-selectorDot" />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MultiMatters;
